import React from 'react';
import { CSSObject } from '@emotion/react';
import { injectTestId } from '@cvent/nucleus-test-automation';
import useTheme from '@cvent/blocks/hooks/useTheme';
import useTimezone from '@hooks/useTimezone';
import type { SessionCardProps } from './SessionCardType';

interface SessionTimeProps {
  start: SessionCardProps['start'];
  end: SessionCardProps['end'];
  showDate: boolean;
  styleOverride?: CSSObject;
  smallerFont?: boolean;
  showMultiDay?: boolean;
  showNumericDate?: boolean;
  noBreak?: boolean;
  showShortNumericDate?: boolean;
}

const useStyles = (smallerFont: boolean): Record<string, CSSObject> => {
  const { font } = useTheme();
  return {
    text: {
      fontSize: smallerFont ? font.base.size[3] : font.base.size.s,
      color: font.color.soft,
      whiteSpace: 'pre-line'
    }
  };
};

function SessionTime({
  start,
  end,
  showDate,
  styleOverride = {},
  smallerFont,
  showMultiDay,
  showNumericDate,
  noBreak,
  showShortNumericDate
}: SessionTimeProps): JSX.Element {
  const { date } = useTimezone();
  const styles = useStyles(smallerFont);

  if (start === '' || end === '') {
    return <div css={styles} />;
  }

  const startDate = new Date(start);
  const endDate = new Date(end);
  const isMultiDaySession = startDate.toDateString() !== endDate.toDateString();
  let startingTime;
  let endingTime;

  if (isMultiDaySession && showNumericDate) {
    startingTime = date(startDate, {
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    });
  } else if (showShortNumericDate) {
    startingTime = date(startDate, {
      weekday: 'short',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    });
  } else if (showDate) {
    startingTime = date(startDate, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    });
  } else {
    startingTime = date(startDate, {
      hour: 'numeric',
      minute: 'numeric'
    });
  }

  if (isMultiDaySession && showNumericDate) {
    endingTime = date(endDate, {
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      timeZoneName: 'short'
    });
  } else if (isMultiDaySession && showShortNumericDate) {
    endingTime = date(endDate, {
      weekday: 'short',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      timeZoneName: 'short'
    });
  } else if (showDate && isMultiDaySession && showMultiDay) {
    endingTime = date(endDate, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      timeZoneName: 'short'
    });
  } else {
    endingTime = date(endDate, {
      hour: 'numeric',
      minute: 'numeric',
      timeZoneName: 'short'
    });
  }

  return isMultiDaySession && showMultiDay && !noBreak ? (
    <span {...injectTestId('session-card-time')} css={[styles.text, styleOverride]}>
      {`${startingTime} -`}
      <br />
      {endingTime}
    </span>
  ) : (
    <span {...injectTestId('session-card-time')} css={[styles.text, styleOverride]}>
      {startingTime} - {endingTime}
    </span>
  );
}

export default SessionTime;
